import type { AxiosInstance } from 'axios';
import type { paths } from '../schema';
import type { RequestOptions, RequestOptionsWithParams } from './types';
import { curry1 } from './utils';

export type IntegrationSecret = Mutable<
  paths['/api/integration-secrets/{id}']['get']['responses']['200']['content']['application/json']
>;

export type GetIntegrationSecretsParams = Omit<
  NonNullable<paths['/api/integration-secrets']['get']['parameters']['query']>,
  'ordering'
> & { ordering?: string };

export type GetIntegrationSecretsResponse =
  paths['/api/integration-secrets']['get']['responses']['200']['content']['application/json'];

export type CreateIntegrationSecretPayload = NonNullable<
  paths['/api/integration-secrets']['post']['requestBody']
>['content']['application/json'];

export type CreateIntegrationSecretResponse =
  paths['/api/integration-secrets']['post']['responses']['201']['content']['application/json'];

export type UpdateIntegrationSecretPayload = NonNullable<
  paths['/api/integration-secrets/{id}']['put']['requestBody']
>['content']['application/json'];

export type UpdateIntegrationSecretResponse =
  paths['/api/integration-secrets/{id}']['put']['responses']['200']['content']['application/json'];

export const getIntegrationSecrets = async (
  instance: AxiosInstance,
  options: RequestOptionsWithParams<GetIntegrationSecretsParams> = {}
): Promise<GetIntegrationSecretsResponse> => {
  const res = await instance.get<GetIntegrationSecretsResponse>(
    '/integration-secrets',
    options
  );
  return res.data;
};

export const getIntegrationSecret = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<IntegrationSecret> => {
  const res = await instance.get<IntegrationSecret>(
    `/integration-secrets/${id}`,
    options
  );
  return res.data;
};

export const createIntegrationSecret = async (
  instance: AxiosInstance,
  payload: CreateIntegrationSecretPayload,
  options: RequestOptions = {}
): Promise<CreateIntegrationSecretResponse> => {
  const res = await instance.post<CreateIntegrationSecretResponse>(
    '/integration-secrets',
    payload,
    options
  );
  return res.data;
};

export const updateIntegrationSecret = async (
  instance: AxiosInstance,
  id: string,
  payload: UpdateIntegrationSecretPayload,
  options: RequestOptions = {}
): Promise<UpdateIntegrationSecretResponse> => {
  const res = await instance.put<UpdateIntegrationSecretResponse>(
    `/integration-secrets/${id}`,
    payload,
    options
  );
  return res.data;
};

export const deleteIntegrationSecret = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<void> => {
  await instance.delete(`/integration-secrets/${id}`, options);
};

export class IntegrationSecretService {
  public getIntegrationSecrets;
  public getIntegrationSecret;
  public createIntegrationSecret;
  public updateIntegrationSecret;
  public deleteIntegrationSecret;

  constructor(private instance: AxiosInstance) {
    this.getIntegrationSecrets = curry1(getIntegrationSecrets)(this.instance);
    this.getIntegrationSecret = curry1(getIntegrationSecret)(this.instance);
    this.createIntegrationSecret = curry1(createIntegrationSecret)(
      this.instance
    );
    this.updateIntegrationSecret = curry1(updateIntegrationSecret)(
      this.instance
    );
    this.deleteIntegrationSecret = curry1(deleteIntegrationSecret)(
      this.instance
    );
  }
}
